/**
 * Avvolge una Promise con un timeout difensivo.
 *
 * Se `promise` non si risolve entro `ms` millisecondi:
 * - se `fallback` e' fornito (anche `undefined`/`null` espliciti), risolve con quel valore;
 * - altrimenti rigetta con un Error di timeout.
 *
 * Serve a impedire che una chiamata di rete appesa blocchi indefinitamente
 * un flusso critico (es. la comparsa del form di login). Implementata con
 * `Promise.race` + `clearTimeout` per non lasciare timer pendenti dopo che
 * la promise originale ha gia' risolto.
 */
const NO_FALLBACK = Symbol('no-fallback');

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  fallback: T | typeof NO_FALLBACK = NO_FALLBACK,
): Promise<T> {
  const hasFallback = fallback !== NO_FALLBACK;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<T>((resolve, reject) => {
    timer = setTimeout(() => {
      if (hasFallback) {
        resolve(fallback as T);
      } else {
        reject(new Error(`Operation timed out after ${ms}ms`));
      }
    }, ms);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
}
